import * as R from 'ramda'
import React, { useState } from 'react'
import NProgress from 'nprogress'
import { useRouter } from 'next/router'
import Link from 'next/link'
import { useSnackbar } from 'notistack'
import Avatar from '@mui/material/Avatar'
import AvatarGroup from '@mui/material/AvatarGroup'
import { useSession } from 'context/Session'
import { withSizes } from 'components/helpers'
import { ToggleButton } from 'components/elements/FollowButton/FollowButton.styles'
import { PostModal } from 'components/collections/Post'
import { getCloudFlareImgVariant } from 'components/helpers/withSizes'
import { ShareBar } from 'components/elements'

import {
  leaveGroup,
  joinGroup,
  doLoadFeed,
  doLoadArticles,
  doLoadQuestions,
  doLoadMembers,
} from '../modules/datasource'

import * as S from './GroupDetail.styles'

const defaultProps = {}
const propTypes = {}

const loadMap = {
  Latest: doLoadFeed,
  Articles: doLoadArticles,
  Questions: doLoadQuestions,
  Members: doLoadMembers,
}

const SITE_URL = process.env.NEXT_PUBLIC_SITE_URL

export const GroupsDetail = ({
  groupId,
  isMobile,
  data,
  membersPreview,
  postData,
  user,
}) => {
  const router = useRouter()
  const { accessToken, loadProfile } = useSession()
  const { enqueueSnackbar } = useSnackbar()
  const { tab = 'Latest', groupSlug, page = 1, post } = router.query
  const [memberType, setMemberType] = useState(data?.member?.type)
  const [customItems, setCustomItems] = useState([])

  const [lastRoute, setLastRoute] = useState(null)

  const isAdmin = data?.member?.type === 'Admin'
  const isMember = memberType && memberType !== 'Pending'
  const isPending = memberType === 'Pending'

  const onPostClose = () => {
    setCustomItems([])
    router.push(lastRoute || `/groups/${groupId}/${groupSlug}`, undefined, {
      shallow: !!lastRoute,
    })
  }

  const onPostOpen = () => {
    setLastRoute(`/groups/${groupId}/${groupSlug}?tab=${tab}&page=${page}`)
  }

  const onPost = ({ postData }) => {
    setCustomItems(i => [{ ...postData, type: 'Post' }, ...i])
  }

  const tabs = [
    {
      label: 'Latest',
      value: 'Latest',
      emptyMessage: 'Nothing to see here',
      config: {
        groupId,
        groupSlug,
      },
    },
    {
      label: 'Articles',
      value: 'Articles',
      emptyMessage: 'No articles to show',
      componentType: 'Article',
    },
    {
      label: 'Questions',
      value: 'Questions',
      emptyMessage: 'No questions to show',
      componentType: 'Question',
    },
    {
      label: 'Members',
      value: 'Members',
      component: p => <S.Member {...p} groupId={groupId} isAdmin={isAdmin} />,
      wrapperOptions: {
        customWrapper: S.CustomWrapper,
      },
      tabPanelOptions: {
        style: { paddingTop: 10 },
      },
    },
  ]

  const changeTab = tab => {
    setCustomItems([])
    window?.scroll(0, 0)
    router.push(`/groups/${groupId}/${groupSlug}?tab=${tab}`, undefined, {
      shallow: true,
    })
  }

  const changePage = page => {
    setCustomItems([])
    window?.scroll(0, 0)
    router.push(
      `/groups/${groupId}/${groupSlug}?tab=${tab}&page=${page}`,
      undefined,
      {
        shallow: true,
      }
    )
  }

  const toggleMember = async () => {
    NProgress.start()
    try {
      if (isMember || isPending) {
        await leaveGroup({
          accessToken: user?.accessToken,
          groupId,
        })
        setMemberType(null)
      } else {
        const { type } = await joinGroup({
          accessToken: user?.accessToken,
          groupId,
        })
        setMemberType(type)
        loadProfile()
      }
    } catch (error) {
      enqueueSnackbar(error.response?.data?.message, { variant: 'error' })
    }
    NProgress.done()
  }

  const customLoad = async ({ type, page, accessToken }) => {
    const getList = loadMap[type]
    if (!getList) {
      return { list: [], total: 0 }
    }
    return await getList({ type, page, accessToken, groupId })
  }

  const onPostDelete = postId => {
    setCustomItems(i => i.filter(({ id }) => Number(id) !== Number(postId)))
  }

  const shareLink = SITE_URL + router.asPath

  return (
    <>
      <S.Container>
        {post && (
          <PostModal
            postId={post}
            data={postData}
            onClose={onPostClose}
            onDelete={onPostDelete}
          />
        )}
        <S.Content>
          <S.Breadcrumb>
            <Link href="/groups" passHref>
              <S.StyledLink>All Groups</S.StyledLink>
            </Link>
            &nbsp;&nbsp;/&nbsp;&nbsp;{data?.name}
          </S.Breadcrumb>
          <S.Header>
            <S.GroupAvatarContainer>
              <S.GroupAvatar>
                {data?.image ? (
                  <S.ImgProfile
                    data-test="icon"
                    src={getCloudFlareImgVariant(data?.image, {
                      default: 'profileDetail',
                    })}
                    alt="article"
                    layout="fill"
                    objectFit="cover"
                    objectPosition="center"
                    quality={90}
                    priority
                    loading="eager"
                  />
                ) : (
                  data && R.head(data?.name)
                )}
              </S.GroupAvatar>
            </S.GroupAvatarContainer>
            <S.HeaderContent>
              <S.Title>{data?.name}</S.Title>
              <S.GroupInfo>
                <S.MembersWrapper>
                  <AvatarGroup max={10}>
                    {R.take(isMobile ? 4 : 10, membersPreview || []).map(
                      ({ profile }) => {
                        const userPath = `/p/${profile?.username}`
                        return (
                          <Link href={userPath} key={profile?.id} passHref>
                            <Avatar
                              as="a"
                              alt={profile?.username}
                              src={profile?.profileImage}
                              style={{ textTransform: 'capitalize' }}
                            >
                              {R.head(profile?.username || 'U')}
                            </Avatar>
                          </Link>
                        )
                      }
                    )}
                  </AvatarGroup>
                  <div>{`${data?.members} member${
                    data?.members === 1 ? '' : 's'
                  }`}</div>
                </S.MembersWrapper>
                <S.GroupDesc>{data?.description}</S.GroupDesc>
              </S.GroupInfo>
              {accessToken && (
                <S.FollowWrapper>
                  <ToggleButton
                    style={{ marginRight: 20, minWidth: 90 }}
                    isActive={isMember || isPending}
                    customLabels={{
                      follow: 'Join Group',
                      following: isPending ? 'Pending' : 'Member',
                      unfollow: 'Leave',
                    }}
                    onClick={toggleMember}
                  />

                  <ShareBar url={shareLink} quote={data?.name} />
                </S.FollowWrapper>
              )}
            </S.HeaderContent>
          </S.Header>
        </S.Content>
      </S.Container>
      {!post && (
        <S.StyledFeed
          CustomHeader={
            isMember && data?.member?.type !== 'Pending' ? (
              <S.StyledPost groupId={groupId} onPost={onPost} />
            ) : null
          }
          customTabs={tabs || []}
          onChangeTab={changeTab}
          onChangePage={changePage}
          onLoadFeed={customLoad}
          onPostOpen={onPostOpen}
          customItems={customItems}
          disableAskButton
          defaultTab="Latest"
          key={groupId}
        />
      )}
    </>
  )
}

GroupsDetail.defaultProps = defaultProps
GroupsDetail.propTypes = propTypes

export default withSizes(GroupsDetail)
